import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Book,
  Zap,
  Code,
  Settings,
  Users,
  Brain,
  Plug,
  Key,
  ArrowRight,
  Search,
  Info,
  Layout,
  RefreshCw,
  Shield,
  HelpCircle,
  ChevronRight,
  Workflow,
  Lightbulb,
} from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const introductionTopics = [
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot', description: 'Learn about our AI-powered employee platform' },
  { title: 'AI Employees explained', href: '/doc/article/ai-employees-explained', description: 'Understand how AI employees work for you' },
  { title: 'SweetPilot Workspaces explained', href: '/doc/article/workspaces-explained', description: 'Organize your work with dedicated workspaces' },
  { title: 'Brain AI explained', href: '/doc/article/brain-ai-explained', description: 'Train your AI with custom knowledge' },
  { title: 'Integrations explained', href: '/doc/article/integrations-explained', description: 'Connect with your favorite tools' },
  { title: 'Automations explained', href: '/doc/article/automations-explained', description: 'Set up automated workflows' },
];

const sections = [
  {
    icon: Zap,
    title: 'Getting Started',
    description: 'Learn the basics and get up and running in minutes.',
    href: '/doc/collections/getting-started',
  },
  {
    icon: Users,
    title: 'AI Employees',
    description: 'Detailed guides for each of our 15 AI employees.',
    href: '/employees',
  },
  {
    icon: Brain,
    title: 'Brain AI',
    description: 'Train your AI employees with custom knowledge.',
    href: '/doc/collections/brain-ai',
  },
  {
    icon: Plug,
    title: 'Integrations',
    description: 'Connect SweetPilot with your favorite tools.',
    href: '/doc/collections/integrations',
  },
  {
    icon: Code,
    title: 'API Reference',
    description: 'Build custom integrations with our REST API.',
    href: '/doc/collections/api-reference',
  },
  {
    icon: Settings,
    title: 'Account & Billing',
    description: 'Manage your account, team, and subscription.',
    href: '/doc/collections/account-billing',
  },
  {
    icon: Workflow,
    title: 'Automations',
    description: 'Set up automated workflows to save time.',
    href: '/doc/collections/automations',
  },
  {
    icon: Lightbulb,
    title: 'Helper Skills (Use Cases)',
    description: 'Discover all the ways AI employees can help you.',
    href: '/doc/collections/helper-skills',
  },
  {
    icon: Shield,
    title: 'Privacy & Security',
    description: 'Learn how we protect your data and privacy.',
    href: '/doc/collections/privacy-security',
  },
  {
    icon: HelpCircle,
    title: 'Troubleshooting & FAQ',
    description: 'Find answers to common questions and issues.',
    href: '/doc/collections/troubleshooting-faq',
  },
];

const popularArticles = [
  { title: 'How to write effective prompts', views: '12.5k views' },
  { title: 'Setting up task automation', views: '8.2k views' },
  { title: 'Training Brain AI with your data', views: '6.7k views' },
  { title: 'Connecting to Slack', views: '5.4k views' },
  { title: 'Understanding credit usage', views: '4.9k views' },
];

export default function Documentation() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title="Documentation"
      subtitle="Everything you need to know to get the most out of SweetPilot."
    >
      {/* Search */}
      <section className="mb-12">
        <div className="relative max-w-2xl mx-auto">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
          <input
            type="text"
            placeholder="Search documentation..."
            className={`w-full pl-12 pr-4 py-4 rounded-2xl text-lg focus:outline-none transition-colors ${
              isDark
                ? 'bg-dark-800 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-purple-500 shadow-sm'
            }`}
          />
        </div>
      </section>

      {/* Introduction Section */}
      <section className="mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-8 rounded-2xl border ${
            isDark
              ? 'bg-dark-800/50 border-white/5'
              : 'bg-white border-slate-200 shadow-sm'
          }`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <Info className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Introduction</h2>
          </div>
          <div className="space-y-1">
            {introductionTopics.map((topic, index) => (
              <motion.div
                key={topic.title}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={topic.href}
                  className={`flex items-center justify-between p-4 rounded-xl transition-all group ${
                    isDark
                      ? 'hover:bg-white/5'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <div>
                    <span className={`font-medium transition-colors ${
                      isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
                    }`}>
                      {topic.title}
                    </span>
                    <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                      {topic.description}
                    </p>
                  </div>
                  <ChevronRight className={`w-5 h-5 transition-colors ${
                    isDark ? 'text-gray-600 group-hover:text-purple-400' : 'text-slate-300 group-hover:text-purple-600'
                  }`} />
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Documentation Sections */}
      <section className="mb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((section, index) => (
            <Link
              key={section.title}
              to={section.href}
              className="block h-full"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-6 rounded-2xl border transition-all h-full group cursor-pointer ${
                  isDark
                    ? 'bg-dark-800/50 border-white/5 hover:border-white/20'
                    : 'bg-white border-slate-200 hover:border-purple-300 shadow-sm'
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
                  <section.icon className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <h3 className={`text-xl font-semibold mb-2 transition-colors ${isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'}`}>{section.title}</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{section.description}</p>
              </motion.div>
            </Link>
          ))}
        </div>
      </section>

      {/* Popular Articles */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Popular Articles</h2>
        <div className="space-y-3">
          {popularArticles.map((article, index) => (
            <motion.div
              key={article.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`flex items-center justify-between p-4 rounded-xl border transition-all group ${
                isDark
                  ? 'bg-dark-800/50 border-white/5 hover:border-purple-500/30'
                  : 'bg-white border-slate-200 hover:border-purple-300 shadow-sm'
              }`}
            >
              <div className="flex items-center gap-4">
                <Book className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                <span className={`transition-colors ${
                  isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
                }`}>
                  {article.title}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>{article.views}</span>
                <ArrowRight className={`w-4 h-4 transition-colors ${
                  isDark ? 'text-gray-500 group-hover:text-purple-400' : 'text-slate-400 group-hover:text-purple-600'
                }`} />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Quick Links */}
      <section className="mb-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className={`p-6 rounded-2xl border ${
            isDark
              ? 'bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/20'
              : 'bg-gradient-to-br from-purple-50 to-transparent border-purple-200 shadow-sm'
          }`}>
            <Key className={`w-8 h-8 mb-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
            <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>API Keys</h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              Generate and manage your API keys for programmatic access.
            </p>
            <Link
              to="/dashboard/settings"
              className={`flex items-center gap-2 font-medium ${
                isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
              }`}
            >
              Manage API Keys <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className={`p-6 rounded-2xl border ${
            isDark
              ? 'bg-gradient-to-br from-cyan-500/10 to-transparent border-cyan-500/20'
              : 'bg-gradient-to-br from-cyan-50 to-transparent border-cyan-200 shadow-sm'
          }`}>
            <Code className={`w-8 h-8 mb-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
            <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>SDKs & Libraries</h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              Official SDKs for JavaScript, Python, Ruby, and more.
            </p>
            <a
              href="https://github.com/flowai"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 font-medium ${
                isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'
              }`}
            >
              View on GitHub <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Can't Find What You're Looking For?</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Our support team is here to help. Reach out and we'll get back to you within 24 hours.
        </p>
        <Link
          to="/help"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
        >
          Contact Support
        </Link>
      </section>
    </StaticPageLayout>
  );
}
